import {Trans} from '@lingui/macro';
import tagifyCss from '@yaireo/tagify/dist/tagify.css';
import {isUndefined} from 'lodash';
import React, {FC, useMemo} from 'react';

import {AsyncHandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {useCss} from '../../../../libs/shared/ui/src/css/cssContext';
import {useNavigationBlocking} from '../../../../srcCommon/app/navigationBlocking';
import {LocalApplicationModel} from '../../../models/api/localApplicationModel';
import {OauthClientModel, UnsavedOauthClientModel} from '../../../models/api/oauthClientModel';
import {SettingsResourcePagesEnum} from '../../../models/url/settingsUrlModel';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {StyledEditorMain, StyledEditorWrapper} from '../../editors/commons/editorCommons';
import {createOauthClientEditorContext} from '../../editors/oauthClientEditor/oauthClientEditorContext';
import {OauthClientEditorState} from '../../editors/oauthClientEditor/oauthClientEditorState';
import {useOauthClientEditorState} from '../../editors/oauthClientEditor/useOauthClientEditorState';
import {DiscardChangesDialog} from '../../rules/ruleEditor/dialog/discardChangesDialog';
import {ResourceEditorFooter} from '../commons/resourceEditorFooter';
import {OauthClientCredentialsCard} from './cards/oauthClientCredentialsCard';
import {OauthClientRedirectUrlsCard} from './cards/oauthClientRedirectUrlsCard';
import {oauthClientEditorFormErrorLabels} from './oauthClientEditorFormErrors';

/*
 * Props.
 */

interface OauthClientEditorWrapperProps {
  panelId: SettingsPanelId;
  application: LocalApplicationModel | undefined;
  oauthClient: OauthClientModel | undefined;
  onSaveOauthClient: AsyncHandlerOf<UnsavedOauthClientModel>;
  navigateTo: (resourcePage: SettingsResourcePagesEnum, id?: number) => void;
}

/*
 * Component.
 */

export const OauthClientEditorWrapper: FC<OauthClientEditorWrapperProps> = (props) => {
  const {panelId, application, oauthClient, onSaveOauthClient, navigateTo} = props;

  // Enable tagify styles.
  useCss(tagifyCss);

  const initialEditorState = useMemo(
    () => OauthClientEditorState.createInitialState(oauthClient),
    [oauthClient],
  );

  const {oauthClientEditorState, ...actions} = useOauthClientEditorState(oauthClient);
  const {oauthClientState} = oauthClientEditorState;

  const hasDiff = OauthClientEditorState.hasDiff(initialEditorState, oauthClientEditorState);

  // Prevent the user from navigating away when there are unsaved changes.
  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(panelId, hasDiff);

  const formErrors = OauthClientEditorState.computeFormErrors(oauthClientEditorState);

  const editorContext = createOauthClientEditorContext({
    namespace: 'global',
    isEditing: !isUndefined(oauthClient),
    hasExpressions: false,
    hasHighlightErrorsFlag: false,
  });

  const onSaveButtonClick = async () => {
    const updatedOauthClient = OauthClientEditorState.toOauthClient(oauthClientEditorState);

    // Save OAuth client.
    await onSaveOauthClient(updatedOauthClient);

    // Unblock navigation.
    unblockNavigation();

    // Navigate back to the main OAuth client page.
    navigateTo(SettingsResourcePagesEnum.OVERVIEW);
  };

  return (
    <>
      <StyledEditorMain>
        <StyledEditorWrapper>
          <OauthClientCredentialsCard application={application} clientId={oauthClientState.clientId} />

          <OauthClientRedirectUrlsCard
            redirectUrls={oauthClientState.redirectUrls}
            onUpdate={actions.onUpdateRedirectUrls}
            isReadOnly={editorContext.isReadOnly}
          />
        </StyledEditorWrapper>
        {didBlockNavigation ? (
          <DiscardChangesDialog onCancel={cancelNavigation} onDiscard={unblockNavigation} />
        ) : null}
      </StyledEditorMain>
      <ResourceEditorFooter
        isEditing={editorContext.isEditing}
        formErrors={formErrors}
        errorLabels={oauthClientEditorFormErrorLabels}
        onCancel={() => navigateTo(SettingsResourcePagesEnum.OVERVIEW)}
        onSave={onSaveButtonClick}
        createLabel={<Trans id="oauth-client-editor-footer-create">Create OAuth client</Trans>}
      />
    </>
  );
};
